import i18next from 'i18next'

/**
 * Supported languages are shared across apps. Namespaces (resources) differ per
 * app and are composed there; the language set + detection live here.
 */

const DEFAULT_LANGUAGE_CODE = 'it' as const

export type AppLanguageCode = 'en' | 'it'

export interface AppLanguage {
	label: string
	code: AppLanguageCode
}

export const DEFAULT_LANGUAGE = {
	label: 'Italiano (IT)',
	code: DEFAULT_LANGUAGE_CODE
} as const satisfies AppLanguage

export const APP_LANGUAGES = [
	DEFAULT_LANGUAGE,
	{ label: 'English (EN)', code: 'en' }
] as const satisfies readonly AppLanguage[]

export function getInitialAppLanguage(): AppLanguageCode {
	if (typeof window === 'undefined') {
		return DEFAULT_LANGUAGE.code
	}

	const documentLanguage = document.documentElement.lang || undefined

	// No browser language here on purpose: a visitor who never chose one reads
	// Italian (DESIGN.md), even on a phone set to English.
	return resolveAppLanguageCode(
		documentLanguage ??
			getStoredLanguage() ??
			getCookieValue(document.cookie, 'i18nextLng')
	)
}

export function detectAppLanguageFromRequest(
	request: Request
): AppLanguageCode {
	// Only the language the visitor chose (cookie); `Accept-Language` is not read,
	// so a visitor with no saved preference sees Italian (DESIGN.md).
	return resolveAppLanguageCode(
		getCookieValue(request.headers.get('cookie'), 'i18nextLng')
	)
}

export function resolveAppLanguageCode(
	value: string | null | undefined
): AppLanguageCode {
	if (!value) {
		return DEFAULT_LANGUAGE.code
	}

	const candidates = value
		.split(',')
		.map((candidate) => candidate.split(';')[0]?.trim().toLowerCase())
		.filter(Boolean)

	for (const candidate of candidates) {
		const language = APP_LANGUAGES.find((appLanguage) => {
			const normalizedCode = appLanguage.code.toLowerCase()
			return (
				candidate === normalizedCode ||
				candidate?.startsWith(`${normalizedCode}-`)
			)
		})

		if (language) {
			return language.code
		}
	}

	return DEFAULT_LANGUAGE.code
}

export function isAppLanguageCode(value: unknown): value is AppLanguageCode {
	return (
		typeof value === 'string' &&
		APP_LANGUAGES.some((language) => language.code === value)
	)
}

export const changeAppLanguage = async (nextLanguage: AppLanguageCode) => {
	if (typeof window !== 'undefined') {
		setStoredLanguage(nextLanguage)
		persistLanguageCookie(nextLanguage)
	}

	await syncAppLanguage(nextLanguage)
}

export function syncAppLanguage(nextLanguage: AppLanguageCode) {
	return i18next.changeLanguage(nextLanguage)
}

function getCookieValue(cookieHeader: string | null, key: string) {
	if (!cookieHeader) {
		return
	}

	const cookies = cookieHeader.split(';')
	for (const cookie of cookies) {
		const [cookieKey, ...valueParts] = cookie.trim().split('=')
		if (cookieKey === key) {
			try {
				return decodeURIComponent(valueParts.join('='))
			} catch {
				return
			}
		}
	}
}

function getStoredLanguage() {
	try {
		return window.localStorage.getItem('i18nextLng')
	} catch {
		return
	}
}

function setStoredLanguage(language: AppLanguageCode) {
	try {
		window.localStorage.setItem('i18nextLng', language)
	} catch {
		// Ignore storage failures; i18next and the cookie fallback still update.
	}
}

function persistLanguageCookie(language: AppLanguageCode) {
	try {
		// biome-ignore lint/suspicious/noDocumentCookie: Cookie Store API is not universally available yet, and this non-sensitive fallback only lets SSR pick the initial language.
		document.cookie = `i18nextLng=${language}; path=/; max-age=31536000; SameSite=Lax`
	} catch {
		// Ignore cookie failures; localStorage/i18next still carry the language.
	}
}
